import i18n from '@/i18n';

type TranslateFn = (key: string, named?: Record<string, unknown>) => string;

/**
 * Get the translate function with simplified typing
 */

const getT = (): TranslateFn => (i18n.global as any).t;

/**
 * Converts various date formats to a Date object
 */
const parseDate = (value: unknown): Date | null => {
  if (value instanceof Date) {
    return value;
  }

  if (typeof value === 'string') {
    // Handle ISO format without timezone (e.g., "2026-02-25T15:50:33.836")
    // Treat as UTC if no timezone specified (server stores dates in UTC)
    let dateString = value;
    const hasIsoDateTimePrefix = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(dateString);
    const hasTimezoneSuffix = /(?:Z|[+-]\d{2}:?\d{2})$/.test(dateString);
    if (hasIsoDateTimePrefix && !hasTimezoneSuffix) {
      dateString += 'Z';
    }
    const parsed = new Date(dateString);
    if (!isNaN(parsed.getTime())) {
      return parsed;
    }
  }

  return null;
};

/**
 * Get the Intl locale string based on current i18n locale
 */
const getLocaleString = (): string => {
  return i18n.global.locale.value === 'en' ? 'en-US' : 'fr-FR';
};

/**
 * Format date to localized string (date only, no time)
 */
export const formatDate = (value: unknown): string => {
  const currentLocale = i18n.global.locale;
  const date = parseDate(value);

  if (date) {
    if (currentLocale.value === 'en') {
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    }

    return date.toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  }

  return String(value);
};

/**
 * Format date and time to localized string
 */
export const formatDateTime = (value: unknown, specificLocale?: string): string => {
  const currentLocale = i18n.global.locale;
  const date = parseDate(value);

  if (date) {
    if (specificLocale) {
      return date.toLocaleString(specificLocale, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    }

    if (currentLocale.value === 'en') {
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    }

    return date.toLocaleString('fr-FR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  return String(value);
};

/**
 * Format a duration between two dates to a human-readable localized string
 */
export const formatDuration = (start: unknown, end: unknown): string => {
  const startDate = parseDate(start);
  const endDate = parseDate(end);

  if (!startDate || !endDate) return '';

  const t = getT();
  const durationMs = endDate.getTime() - startDate.getTime();
  const durationMinutes = Math.floor(durationMs / (1000 * 60));

  if (durationMinutes >= 60 * 24) {
    const days = Math.floor(durationMinutes / (60 * 24));
    const remainingHours = Math.floor((durationMinutes % (60 * 24)) / 60);

    if (remainingHours === 0) {
      return days === 1 ? t('common.duration.oneDay') : t('common.duration.days', { count: days });
    }
    return t('common.duration.daysHours', { days, hours: remainingHours });
  }

  const hours = Math.floor(durationMinutes / 60);
  const minutes = durationMinutes % 60;

  if (hours === 0) {
    return t('common.duration.minutes', { minutes });
  }

  return minutes === 0
    ? t('common.duration.hours', { hours })
    : t('common.duration.hoursMinutes', {
        hours,
        minutes: minutes.toString().padStart(2, '0'),
      });
};

/**
 * Normalize an ISO string to ensure it's interpreted as UTC.
 * Adds 'Z' suffix if no timezone indicator is present.
 */
export const normalizeIsoString = (iso: string): string => {
  if (!iso) return iso;
  const hasTimezone = iso.endsWith('Z') || iso.includes('+') || iso.includes('-', 10);
  const hasTime = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(iso);
  return hasTime && !hasTimezone ? iso + 'Z' : iso;
};

/**
 * Parse an ISO datetime string (assumed UTC if no tz) and return the local date as YYYY-MM-DD.
 */
export const parseIsoToLocalDate = (iso: string): string | null => {
  if (!iso) return null;
  const d = new Date(normalizeIsoString(iso));
  if (isNaN(d.getTime())) return null;
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Parse an ISO datetime string (assumed UTC if no tz) and return the local time as HH:mm.
 */
export const parseIsoToLocalTime = (iso: string): string | null => {
  if (!iso) return null;
  const d = new Date(normalizeIsoString(iso));
  if (isNaN(d.getTime())) return null;
  const hours = d.getHours().toString().padStart(2, '0');
  const minutes = d.getMinutes().toString().padStart(2, '0');
  return `${hours}:${minutes}`;
};

/**
 * Convert a local date (YYYY-MM-DD) and time (HH:mm) to an ISO UTC string.
 */
export const localToIsoUtc = (date: string, time: string = '00:00'): string => {
  const localDate = new Date(`${date}T${time}:00`);
  return localDate.toISOString();
};

/**
 * Format a date range to a human-readable localized string
 */
export const formatDateRange = (start: unknown, end: unknown): string => {
  const startDate = parseDate(start);
  const endDate = parseDate(end);

  if (!startDate || !endDate) return '';

  const t = getT();
  const locale = getLocaleString();

  const dateOptions: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  };
  const timeOptions: Intl.DateTimeFormatOptions = {
    hour: '2-digit',
    minute: '2-digit',
  };

  const isSameDay = startDate.toDateString() === endDate.toDateString();

  if (isSameDay) {
    return t('common.dateRange.sameDay', {
      day: startDate.toLocaleDateString(locale, dateOptions),
      from: startDate.toLocaleTimeString(locale, timeOptions),
      to: endDate.toLocaleTimeString(locale, timeOptions),
    });
  }

  return t('common.dateRange.severalDays', {
    startDay: startDate.toLocaleDateString(locale, dateOptions),
    startTime: startDate.toLocaleTimeString(locale, timeOptions),
    endDay: endDate.toLocaleDateString(locale, dateOptions),
    endTime: endDate.toLocaleTimeString(locale, timeOptions),
  });
};
